/**
 * Phase-2 HQ hero: the Animus Core and roof oculus.
 *
 * This module owns only world presentation. Live daemon/run projection is
 * intentionally injected later by P7; Portal and runtime concepts do not
 * belong in this geometry builder.
 */
import * as pc from 'playcanvas'
import { worldRoot } from './worldRoot'
import { createRenderEntity } from './interiorShared'

export const CORE_X = 4
export const CORE_Z = 0
export const CORE_ORB_Y = 9
export const CORE_HEART_DIAMETER = 2.4
export const CORE_LATTICE_DIAMETER = 3.2
export const CORE_MAST_BOTTOM = 0.3
export const CORE_MAST_TOP = 17.5
export const CORE_MAST_SEGMENTS = 8
/** Slender enough to preserve cross-atrium views at normal FPV eye height. */
export const CORE_MAST_DIAMETER = 0.72
export const CORE_BEAD_CAP = 24
export const CORE_SHELL_GEOMETRY = 'geodesic-icosahedron' as const
export const CORE_SHELL_TRIANGLES = 20
/** PlayCanvas RENDERSTYLE_WIREFRAME; kept numeric so headless mocks stay lean. */
export const CORE_SHELL_RENDER_STYLE = 1
export const CORE_LATTICE_OPACITY = 0.25
export const CORE_BANNER_SIZE = [2, 9, 0.05] as const
export const CORE_BANNER_SWAY_DEGREES = 2.5
export const OCULUS_SIZE = [8, 7] as const
export const OCULUS_FRAME_BAR_WIDTH = 0.16
export const CORE_FILAMENT_DIAMETER = 0.02

export type CorePoint = readonly [number, number, number]

/**
 * Roof-aperture corners map out to the matching L1 void-rail corners. The
 * slight flare makes all four strands readable from the medallion without
 * adding intermediate entities.
 */
export const CORE_FILAMENT_ENDPOINTS: readonly {
  top: CorePoint
  bottom: CorePoint
}[] = [
  { top: [0, 18, -3.5], bottom: [-3, 5.6, -6] },
  { top: [8, 18, -3.5], bottom: [11, 5.6, -6] },
  { top: [0, 18, 3.5], bottom: [-3, 5.6, 6] },
  { top: [8, 18, 3.5], bottom: [11, 5.6, 6] },
] as const

export interface HqCoreHandles {
  mastMaterial: pc.StandardMaterial
  orbMaterial: pc.StandardMaterial
  haloEntities: readonly pc.Entity[]
  beadPool: readonly pc.Entity[]
  oculusLight: pc.Entity
  setHaloSpeedMultiplier(value: number): void
  destroy(): void
}

function material(
  diffuse: pc.Color,
  emissive?: pc.Color,
  emissiveIntensity = 1,
): pc.StandardMaterial {
  const mat = new pc.StandardMaterial()
  mat.diffuse = diffuse
  mat.useMetalness = true
  mat.metalness = emissive ? 0.45 : 0.1
  mat.gloss = emissive ? 0.8 : 0.35
  if (emissive) {
    mat.emissive = emissive
    mat.emissiveIntensity = emissiveIntensity
  }
  mat.update()
  return mat
}

function transparentMaterial(
  diffuse: pc.Color,
  emissive: pc.Color,
  opacity: number,
): pc.StandardMaterial {
  const mat = material(diffuse, emissive, 1.35)
  mat.opacity = opacity
  mat.blendType = pc.BLEND_NORMAL
  mat.depthWrite = false
  mat.cull = pc.CULLFACE_NONE
  mat.update()
  return mat
}

function corePrimitive(
  parent: pc.Entity,
  name: string,
  type: 'box' | 'sphere' | 'cylinder' | 'cone' | 'plane',
  position: readonly [number, number, number],
  scale: readonly [number, number, number],
  mat: pc.Material,
): pc.Entity {
  const entity = createRenderEntity({
    parent,
    name,
    type,
    position,
    scale,
    material: mat,
  })
  if (entity.render) entity.render.castShadows = false
  return entity
}

/** One flat-shaded triangle per icosahedron face: faceted in a single draw. */
export function coreShellMeshData(radius = CORE_LATTICE_DIAMETER / 2): {
  positions: number[]
  normals: number[]
  indices: number[]
} {
  const phi = (1 + Math.sqrt(5)) / 2
  const source = [
    [-1, phi, 0], [1, phi, 0], [-1, -phi, 0], [1, -phi, 0],
    [0, -1, phi], [0, 1, phi], [0, -1, -phi], [0, 1, -phi],
    [phi, 0, -1], [phi, 0, 1], [-phi, 0, -1], [-phi, 0, 1],
  ] as const
  const vertices = source.map(([x, y, z]) => {
    const scale = radius / Math.hypot(x, y, z)
    return [x * scale, y * scale, z * scale] as const
  })
  const faces = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ] as const
  const positions: number[] = []
  const normals: number[] = []
  const indices: number[] = []
  for (const face of faces) {
    const base = positions.length / 3
    const [a, b, c] = face.map((vertexIndex) => vertices[vertexIndex])
    const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]] as const
    const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]] as const
    const nx = ab[1] * ac[2] - ab[2] * ac[1]
    const ny = ab[2] * ac[0] - ab[0] * ac[2]
    const nz = ab[0] * ac[1] - ab[1] * ac[0]
    const inverseLength = 1 / Math.hypot(nx, ny, nz)
    const normal = [nx * inverseLength, ny * inverseLength, nz * inverseLength] as const
    for (const vertexIndex of face) {
      positions.push(...vertices[vertexIndex])
      normals.push(...normal)
    }
    indices.push(base, base + 1, base + 2)
  }
  return { positions, normals, indices }
}

/** Transform a unit-Y cylinder so it joins two world-space points. */
export function filamentTransform(start: CorePoint, end: CorePoint): {
  position: CorePoint
  length: number
  rotation: readonly [number, number, number, number]
  euler: readonly [number, number, number]
} {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const dz = end[2] - start[2]
  const length = Math.hypot(dx, dy, dz)
  const ux = dx / length
  const uy = dy / length
  const uz = dz / length
  // Quaternion rotating local +Y to the segment direction. None of the HQ
  // filaments are antiparallel to +Y, so the stable shortest-arc form applies.
  const qx = uz
  const qy = 0
  const qz = -ux
  const qw = 1 + uy
  const inverseQuaternionLength = 1 / Math.hypot(qx, qy, qz, qw)
  const rotation = [
    qx * inverseQuaternionLength,
    qy,
    qz * inverseQuaternionLength,
    qw * inverseQuaternionLength,
  ] as const
  const [rx, ry, rz, rw] = rotation
  const yTerm = 2 * (rw * ry - rx * rz)
  const toDegrees = 180 / Math.PI
  return {
    position: [
      (start[0] + end[0]) / 2,
      (start[1] + end[1]) / 2,
      (start[2] + end[2]) / 2,
    ],
    length,
    rotation,
    euler: [
      Math.atan2(2 * (rw * rx + ry * rz), 1 - 2 * (rx * rx + ry * ry)) * toDegrees,
      Math.asin(Math.max(-1, Math.min(1, yTerm))) * toDegrees,
      Math.atan2(2 * (rw * rz + rx * ry), 1 - 2 * (ry * ry + rz * rz)) * toDegrees,
    ],
  }
}

function applyBannerWordmark(
  app: pc.Application,
  bannerMaterial: pc.StandardMaterial,
): pc.Texture | null {
  // jsdom exposes a canvas element but no 2D renderer unless its optional
  // native canvas package is installed. Treat that as the same graceful
  // headless fallback as a missing document instead of invoking its noisy stub.
  if (typeof document === 'undefined' || typeof CanvasRenderingContext2D === 'undefined') return null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 1024
    const context = canvas.getContext('2d')
    if (!context) return null
    context.fillStyle = '#1f1533'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.strokeStyle = '#6e58a5'
    context.lineWidth = 6
    context.strokeRect(18, 18, canvas.width - 36, canvas.height - 36)
    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate(-Math.PI / 2)
    context.fillStyle = '#b99bea'
    context.font = '700 82px Nunito, system-ui, sans-serif'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.letterSpacing = '14px'
    context.fillText('ANIMUS', 0, 4)
    context.restore()
    const texture = new pc.Texture(app.graphicsDevice, {
      width: canvas.width,
      height: canvas.height,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    })
    texture.setSource(canvas)
    bannerMaterial.emissiveMap = texture
    bannerMaterial.emissive = new pc.Color(1, 1, 1)
    bannerMaterial.emissiveIntensity = 1.1
    bannerMaterial.update()
    return texture
  } catch {
    // Canvas is intentionally optional in headless/unit-test environments.
    // The shared emissive cloth material remains a complete visual fallback.
    return null
  }
}

function geodesicShell(
  app: pc.Application,
  parent: pc.Entity,
  mat: pc.Material,
): pc.Entity {
  const data = coreShellMeshData()
  const geometry = {
    positions: data.positions,
    normals: data.normals,
    indices: data.indices,
  } as pc.Geometry
  const mesh = pc.Mesh.fromGeometry(app.graphicsDevice, geometry)
  const meshInstance = new pc.MeshInstance(mesh, mat)
  meshInstance.renderStyle = CORE_SHELL_RENDER_STYLE
  const entity = new pc.Entity('hq-core-shell')
  entity.addComponent('render', { meshInstances: [meshInstance] })
  if (entity.render) entity.render.castShadows = false
  entity.setLocalPosition(CORE_X, CORE_ORB_Y, CORE_Z)
  parent.addChild(entity)
  return entity
}

function torus(
  app: pc.Application,
  parent: pc.Entity,
  name: string,
  ringRadius: number,
  rotation: readonly [number, number, number],
  mat: pc.Material,
): pc.Entity {
  const geometry = new pc.TorusGeometry({
    ringRadius,
    tubeRadius: 0.12,
    segments: 40,
    sides: 8,
  })
  const mesh = pc.Mesh.fromGeometry(app.graphicsDevice, geometry)
  const entity = new pc.Entity(name)
  entity.addComponent('render', { meshInstances: [new pc.MeshInstance(mesh, mat)] })
  if (entity.render) entity.render.castShadows = false
  entity.setLocalPosition(CORE_X, CORE_ORB_Y, CORE_Z)
  entity.setLocalEulerAngles(rotation[0], rotation[1], rotation[2])
  parent.addChild(entity)
  return entity
}

/**
 * Build the full mast → heart → rings → oculus reveal on the atrium axis.
 * The returned materials and bead pool are the stable P7 data-binding seam.
 */
export function buildHqCore(app: pc.Application): HqCoreHandles {
  const world = worldRoot(app)
  const root = new pc.Entity('hq-core-root')
  world.addChild(root)
  const staticBatchGroup = app.batcher?.addGroup('hq-core:static', false, 32)
  const staticPrimitive = (
    name: string,
    type: 'box' | 'sphere' | 'cylinder' | 'cone' | 'plane',
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    mat: pc.Material,
  ): pc.Entity => {
    const entity = corePrimitive(root, name, type, position, scale, mat)
    if (staticBatchGroup && entity.render) {
      entity.render.batchGroupId = staticBatchGroup.id
    }
    return entity
  }
  const healthy = new pc.Color(0.21, 0.75, 0.48)
  const cyan = new pc.Color(0.20, 0.84, 0.90)
  const bronze = new pc.Color(0.45, 0.28, 0.12)
  const mastMaterial = material(new pc.Color(0.05, 0.14, 0.12), healthy, 2.2)
  const orbMaterial = material(new pc.Color(0.06, 0.18, 0.20), cyan, 2.8)
  const haloMaterial = material(new pc.Color(0.07, 0.18, 0.18), cyan, 2.4)
  const bronzeMaterial = material(bronze, new pc.Color(0.44, 0.23, 0.08), 0.45)
  const medallionMaterial = material(
    new pc.Color(0.10, 0.24, 0.18),
    healthy,
    2.0,
  )
  const glassMaterial = transparentMaterial(
    new pc.Color(0.35, 0.55, 0.62),
    new pc.Color(0.12, 0.55, 0.62),
    0.18,
  )
  // The oculus is an environment reveal, not a luminous ceiling panel. Keep
  // one lightly tinted, unlit, double-sided pane so the real sky remains the
  // dominant read through the opening.
  glassMaterial.useLighting = false
  glassMaterial.update()
  const shellMaterial = transparentMaterial(
    new pc.Color(0.18, 0.50, 0.58),
    cyan,
    CORE_LATTICE_OPACITY,
  )
  const bannerMaterial = transparentMaterial(
    new pc.Color(0.12, 0.08, 0.20),
    new pc.Color(0.24, 0.16, 0.42),
    0.72,
  )
  const bannerTexture = applyBannerWordmark(app, bannerMaterial)

  const segmentSpan = (CORE_MAST_TOP - CORE_MAST_BOTTOM) / CORE_MAST_SEGMENTS
  const segmentHeight = segmentSpan - 0.08
  for (let i = 0; i < CORE_MAST_SEGMENTS; i++) {
    staticPrimitive(
      `hq-core-mast-${i}`,
      'box',
      [CORE_X, CORE_MAST_BOTTOM + segmentSpan * (i + 0.5), CORE_Z],
      [CORE_MAST_DIAMETER, segmentHeight, CORE_MAST_DIAMETER],
      mastMaterial,
    )
  }

  staticPrimitive('hq-core-medallion', 'cylinder', [CORE_X, 0.035, CORE_Z], [6.5, 0.07, 6.5], medallionMaterial)
  corePrimitive(
    root,
    'hq-core-heart',
    'sphere',
    [CORE_X, CORE_ORB_Y, CORE_Z],
    [CORE_HEART_DIAMETER, CORE_HEART_DIAMETER, CORE_HEART_DIAMETER],
    orbMaterial,
  )
  geodesicShell(app, root, shellMaterial)

  const haloEntities = [
    torus(app, root, 'hq-core-halo-0', 2.0, [0, 0, 0], haloMaterial),
    torus(app, root, 'hq-core-halo-1', 2.5, [58, 0, 18], haloMaterial),
    torus(app, root, 'hq-core-halo-2', 3.0, [18, 0, 67], haloMaterial),
  ]

  staticPrimitive('hq-core-crown', 'cone', [CORE_X, 17.68, CORE_Z], [1.8, 0.9, 1.8], bronzeMaterial)
  const banners: pc.Entity[] = []
  // Keep the north/south banners off the reveal axis so the heart, mast and
  // oculus remain legible from the medallion and the L3 balustrade.
  const bannerPositions = [
    [0, -5.85],
    [8, 5.85],
  ] as const
  for (const [index, [x, z]] of bannerPositions.entries()) {
    const banner = corePrimitive(
      root,
      `hq-core-banner-${index}`,
      'box',
      [x, 13, z],
      CORE_BANNER_SIZE,
      bannerMaterial,
    )
    banner.setLocalEulerAngles(index === 0 ? -CORE_BANNER_SWAY_DEGREES : CORE_BANNER_SWAY_DEGREES, 0, 0)
    banners.push(banner)
  }

  // The glass is deliberately one pane: the four surrounding roof bands are
  // opaque, so this adds only one transparent layer over the sky reveal.
  const oculus = corePrimitive(
    root,
    'hq-oculus-glass',
    'plane',
    [CORE_X, 18.015, CORE_Z],
    [OCULUS_SIZE[0], 1, OCULUS_SIZE[1]],
    glassMaterial,
  )
  oculus.setLocalEulerAngles(0, 0, 0)

  // Four narrow bars frame the rectangular 8×7 aperture without reducing its
  // clear dimensions. Sharing mastMaterial makes the ring health-tinted via
  // the existing P7 material handle, with no additional binding surface.
  const frameY = 18.035
  const halfWidth = OCULUS_SIZE[0] / 2
  const halfDepth = OCULUS_SIZE[1] / 2
  const frameOffset = OCULUS_FRAME_BAR_WIDTH / 2
  staticPrimitive('hq-oculus-frame-west', 'box', [CORE_X - halfWidth - frameOffset, frameY, CORE_Z], [OCULUS_FRAME_BAR_WIDTH, 0.1, OCULUS_SIZE[1] + OCULUS_FRAME_BAR_WIDTH * 2], mastMaterial)
  staticPrimitive('hq-oculus-frame-east', 'box', [CORE_X + halfWidth + frameOffset, frameY, CORE_Z], [OCULUS_FRAME_BAR_WIDTH, 0.1, OCULUS_SIZE[1] + OCULUS_FRAME_BAR_WIDTH * 2], mastMaterial)
  staticPrimitive('hq-oculus-frame-north', 'box', [CORE_X, frameY, CORE_Z + halfDepth + frameOffset], [OCULUS_SIZE[0], 0.1, OCULUS_FRAME_BAR_WIDTH], mastMaterial)
  staticPrimitive('hq-oculus-frame-south', 'box', [CORE_X, frameY, CORE_Z - halfDepth - frameOffset], [OCULUS_SIZE[0], 0.1, OCULUS_FRAME_BAR_WIDTH], mastMaterial)

  for (const [index, endpoints] of CORE_FILAMENT_ENDPOINTS.entries()) {
    const transform = filamentTransform(endpoints.bottom, endpoints.top)
    const filament = staticPrimitive(
      `hq-core-filament-${index}`,
      'cylinder',
      transform.position,
      [CORE_FILAMENT_DIAMETER, transform.length, CORE_FILAMENT_DIAMETER],
      haloMaterial,
    )
    filament.setLocalEulerAngles(...transform.euler)
  }

  const oculusLight = new pc.Entity('hq-oculus-down')
  oculusLight.addComponent('light', {
    type: 'spot',
    color: new pc.Color(1.0, 0.72, 0.46),
    intensity: 2.0,
    range: 22,
    innerConeAngle: 25,
    outerConeAngle: 40,
    castShadows: false,
  })
  oculusLight.setLocalPosition(CORE_X, 17.85, CORE_Z)
  root.addChild(oculusLight)

  // Pooled now, activated by the P7 projection later. Keeping the entities
  // stable avoids per-run allocations and makes the cap enforceable.
  const beadPool: pc.Entity[] = []
  for (let i = 0; i < CORE_BEAD_CAP; i++) {
    const bead = corePrimitive(
      root,
      `hq-core-bead-${i}`,
      'sphere',
      [CORE_X, CORE_MAST_BOTTOM, CORE_Z],
      [0.2, 0.2, 0.2],
      haloMaterial,
    )
    bead.enabled = false
    beadPool.push(bead)
  }

  let haloSpeedMultiplier = 1
  let elapsed = 0
  const spin = (dt: number): void => {
    elapsed += dt
    const rates = [8, -5, 3.5]
    for (let i = 0; i < haloEntities.length; i++) {
      const halo = haloEntities[i]
      const euler = halo.getLocalEulerAngles()
      halo.setLocalEulerAngles(
        euler.x,
        euler.y + rates[i] * haloSpeedMultiplier * dt,
        euler.z,
      )
    }
    for (let i = 0; i < banners.length; i++) {
      banners[i].setLocalEulerAngles(
        Math.sin(elapsed * 0.45 + i * Math.PI) * CORE_BANNER_SWAY_DEGREES,
        0,
        0,
      )
    }
  }
  const events = app as pc.Application & {
    on?: (name: string, cb: (dt: number) => void) => void
    off?: (name: string, cb: (dt: number) => void) => void
  }
  events.on?.('update', spin)

  return {
    mastMaterial,
    orbMaterial,
    haloEntities,
    beadPool,
    oculusLight,
    setHaloSpeedMultiplier(value): void {
      haloSpeedMultiplier = Number.isFinite(value)
        ? Math.max(0.15, Math.min(4, value))
        : 0.35
    },
    destroy(): void {
      events.off?.('update', spin)
      bannerTexture?.destroy()
    },
  }
}
